import {
  Component,
  ElementRef,
  // EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  // Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  finalize,
  forkJoin,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  tap,
  EMPTY,
  map,
  merge,
  mergeMap, filter,
} from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { Moment } from 'moment-timezone/moment-timezone';

import { ICapacity, Resource, ResourceAssignedToFloorplan } from '@shared/resource/models';
import { Schedule } from '@shared/schedule/models';
import { Party } from '@shared/party/models';
import { Service } from '@shared/service/models';
import { PartySource, PartyTypes } from '@shared/party/enums';
import { Restaurant } from '@shared/restaurant/models';
import { isUsOrCaPhoneNumber, useWhatsApp } from '@shared/sms/functions';
import { mergeCustomFields } from '@shared/party';
import { MS_PER_MIN } from '@shared/core/constants';
import { isOnFreePlan } from '@shared/user/functions';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import {
  CustomDateParserFormatterProvider,
  CustomTimepickerModelAdapterProvider,
  DateTimeService,
  IParty,
  ProviderScheduleService,
  RESOURCE_ADJUSTMENT_ITEMS,
  TelService,
  ValidationService,
} from '@tablesready/common-ui';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { DateService, MarkPartyService, PartiesService, WaitTimeService } from '../../../services';
import { CustomersService } from '../../../customers/services';

@Component({
  selector: 'tablesready-add-edit-party-reactive',
  templateUrl: './add-edit-party-reactive.component.html',
  styleUrls: ['./add-edit-party-reactive.component.scss'],
  providers: [
    CustomDateParserFormatterProvider,
    CustomTimepickerModelAdapterProvider,
  ],
})
// TODO: disable date and time if selectedTimeMode is true
export class AddEditPartyReactiveComponent implements OnInit, OnDestroy {
  @Input() partyToEdit: IParty;
  @Input() userSchedule;
  @Input() user: Restaurant;
  @Input() parties: IParty[];
  @Input() selectedServices;
  @Input() showSelectResourceMessage: boolean;
  @Input() selectedResource: Resource;
  @Input() selectedTime: number;
  @Input() selectedEndTime: number;
  @Input() selectedDate: NgbDateStruct;
  @Input() transparentMode = false;
  @Input() afterInit?: () => void;
  // @Output() resourceOverride = new EventEmitter<IParty>();

  title = 'Add';
  angularticsActionType: string;
  selectedTimeMode = false;
  isSubmitting = false;
  isSavingParty = false;
  resourcesInDropdown: ResourceAssignedToFloorplan[] = [];
  servicesInDropdown: Service[] = [];
  shortestPhoneDigits = 4;
  originalQuotedTime = 0;
  timezone: string;
  currentTime: Moment;
  isWorkTime = false;
  showResourcesSizeAlert = false;
  deltaResourcesSize = 0;
  determiner: 'less' | 'more' = 'more';
  determinerChange: 'decrease' | 'increase' = 'decrease';
  determinerFix: RESOURCE_ADJUSTMENT_ITEMS = RESOURCE_ADJUSTMENT_ITEMS.add;
  generalSchedule: Schedule[];
  reservationMinutesPast = 420;
  icon = AppIcon;
  workHours: Array<[number, number]>;
  showOverlappingAlert;
  showResourceAlert = false;
  availablePartyDuration;
  availablePartyDurationInMin;
  removeServices = false;
  removeResources = false;
  overrideNoResources = false;
  overrideIsWorkTime = false;
  // Date
  minDate: NgbDateStruct;
  maxDate: NgbDateStruct;
  noteOptions: string[] | string;
  serviceSettingsLabel: string;
  resourceSettingsLabel: string;
  displayOptionCustomerLabel: string;
  displayOptionsReservationsLabel: string;
  servicesEnabled: boolean;
  form: UntypedFormGroup;
  noAvailableServices = false;
  isAvailableTime: boolean = false;
  isSubmitDisabled = true;

  private overlappingParty: Party[] = [];
  private allowMultipleParties: boolean;
  private resourcesEnabled: boolean;
  private onlineReservationEnabled: boolean;
  private partySizeForTimeSlots: boolean;
  private isEditMode = false;
  private readonly lockedControlNames: string[] = ['services', 'resources', 'date', 'time'];

  @ViewChild('confirmModalContent') confirmModalContentRef: ElementRef;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly alertsService: AlertsService,
    private readonly waitTimeService: WaitTimeService,
    private readonly dateService: DateService,
    private readonly partiesService: PartiesService,
    private readonly modalController: FrontendModalService,
    private readonly telService: TelService,
    private readonly dateTimeService: DateTimeService,
    private readonly providerScheduleService: ProviderScheduleService,
    private readonly markPartyService: MarkPartyService,
    private readonly customerService: CustomersService,
    private readonly formBuilder: FormBuilder,
    private readonly validationService: ValidationService
  ) { }

  isOutOfBusinessHours(): boolean {
    return (this.servicesEnabled && !this.isWorkTime || this.isPartyInPastTime) && this.isTimeFieldValid();
  }

  isTimeFieldValid(): boolean {
    const time = this.getGroupField('time')?.getRawValue();

    return time !== '' && time !== undefined;
  }

  getNoAvailableServices(): boolean {
    return this.servicesEnabled &&
      this.servicesInDropdown.length === 0 &&
      this.isTimeFieldValid() &&
      !this.isOutOfBusinessHours();
  }

  getIsAvailableTime() {
    return this.isWorkTime &&
      this.showResourceAlert &&
      !this.resourcesInDropdown.length &&
      this.resourcesEnabled;
  }

  get isPartyInPastTime() {
    return this.currentTime.valueOf() < Date.now();
  }

  get isLocationOnFreePlan(): boolean {
    return isOnFreePlan({ account: this.user.account, location: this.user });
  }

  ngOnInit(): void {
    this.initModalProp();
    this.initForm();
    this.minDate = this.dateTimeService.momentToNgbDate(this.currentTime);
    this.maxDate = {
      ...this.minDate,
      year: this.minDate.year + 1,
    };
    if (this.partyToEdit) {
      this.initEditMode();
    } else if (this.selectedTime && this.selectedResource) {
      this.initAddModeWithSelectedResourceAndTime(this.selectedTime);
    } else {
      this.initAddMode();
    }

    this.title += ` ${this.user.displayOptions?.labels?.reservation || 'reservation'}`;
  }

  initModalProp() {
    this.noteOptions = this.user?.noteOptions || [];
    this.serviceSettingsLabel = this.user?.serviceSettings?.label || 'service';
    this.resourceSettingsLabel = this.user.resourceSettings?.label || 'resource';
    this.displayOptionCustomerLabel = this.user.displayOptions?.labels?.customer || 'customer';
    this.displayOptionsReservationsLabel = this.user.displayOptions?.labels?.reservation || 'reservation';
    this.allowMultipleParties = this.user?.bookingOptions?.allowMultipleParties;
    this.servicesEnabled = this.user?.serviceSettings?.enabled;
    this.resourcesEnabled = this.user?.resourceSettings?.enabled;
    this.onlineReservationEnabled = this.user?.bookingOptions?.onlineReservationsEnabled;
    this.partySizeForTimeSlots = this.user?.bookingOptions?.partySizeForTimeSlots;
    this.timezone = this.user.timezone;
    this.generalSchedule = (this.userSchedule || []).filter(schedule => schedule.default);

    try {
      this.generalSchedule[0].hours;
    } catch (e) {
      this.alertsService.add({
        time: Date.now(),
        type: IAlertTypes.danger,
        heading: 'Please fill in work schedule in Settings -> Schedules',
        body: '',
      });
    }

    this.currentTime = moment.tz(this.timezone);
    this.showSelectResourceMessage = this.showSelectResourceMessage || false;
    this.overrideNoResources = !(this.user.serviceSettings?.enabled || this.user.bookingOptions?.onlineReservationsEnabled);
  }

  initEditMode() {
    this.title = 'Edit ';
    this.angularticsActionType = 'edited reservation';
    this.selectedTimeMode = false;
    this.isEditMode = true;
    const quotedTime = moment.tz(this.partyToEdit.quoted_time, this.timezone);
    const data = {
      ...this.partyToEdit,
      phone: this.telService.format(this.partyToEdit.phone, this.user.country, 'custom'),
      requestGroup: {
        ...this.partyToEdit,
        date: this.dateTimeService.momentToNgbDate(quotedTime),
        time: quotedTime.format('h:mm A'),
        overrideNoResources: this.overrideNoResources, // TODO: consider removing component property
        overrideIsWorkTime: this.overrideIsWorkTime, // TODO: consider removing component property
      },
    };
    this.originalQuotedTime = this.partyToEdit.quoted_time;
    this.currentTime = moment(this.partyToEdit.quoted_time).tz(this.timezone);
    this.isWorkTime = true;
    this.form.patchValue(data, {emitEvent: false});
    if (!this.partyToEdit.phone || isUsOrCaPhoneNumber(this.partyToEdit.phone) || this.partyToEdit?.source === PartySource.api) {
      this.form.get('messagingProvider').disable({onlySelf: true, emitEvent: false});
    } else {
      this.form.get('messagingProvider').enable({onlySelf: true, emitEvent: false});
    }
    this.subscriptions.add(
      this.checkWorkTime(data.requestGroup)
        .pipe(
          tap(() => {
            this.handleAlerts();
            this.isSubmitting = false;
            this.setFlags();
          }),
        )
        .subscribe()
    );
  }

  initAddModeWithSelectedResourceAndTime(time: number): void {
    this.angularticsActionType = 'added reservation';
    this.selectedTimeMode = true;
    const selectedTime = this.dateTimeService.roundingMinutes(Number(time), 15, this.timezone);
    const party = {
      quoted_time: 0,
      type: PartyTypes.reservation,
      date: this.selectedDate,
      time: selectedTime.format('h:mm A'),
      resources: [this.selectedResource._id],
      services: [],
      customFields: {},
      size: Math.max(this.selectedResource.capacity.min, Math.min(this.user.default_size, this.selectedResource.capacity.max)),
    };
    this.currentTime = selectedTime;
    this.form.patchValue(party);
  }

  initAddMode() {
    this.angularticsActionType = 'added reservation';
    this.selectedTimeMode = false;
    let data = {
      type: PartyTypes.reservation,
      customFields: {},
      requestGroup: {
        date: this.selectedDate,
        time: '',
        size: this.user.default_size,
        services: [],
        resources: [],
        duration: 0,
        overrideNoResources: this.overrideNoResources, // TODO: consider removing component property
        overrideIsWorkTime: this.overrideIsWorkTime, // TODO: consider removing component property
      },
    };
    this.form.patchValue(data, {emitEvent: false});
  }

  checkWorkTime(values: Record<string, unknown> = {}): Observable<boolean> {
    if (!this.servicesEnabled && this.resourcesEnabled) {
       return this.getAvailableResourcesServices(values);
    }
    if (!values.time) {
      // this.resourceOverride.emit(values);

      return of(false);
    }
    const { hours, exceptions } = this.generalSchedule[0];
    this.workHours = this.providerScheduleService.getWorkingHours(hours, exceptions, this.currentTime.valueOf(), this.timezone);

    let duration = this.getGroupField('duration').value;
    if (duration <= 0) {
      duration = this.dateService.checkServicesDuration(this.servicesInDropdown, values.services as string[]);
      this.getGroupField('duration').setValue(duration, {emitEvent: false});
    }

    this.isWorkTime = this.providerScheduleService.comparisonTime(this.currentTime.valueOf(), this.workHours, values.duration as number);
    if (this.servicesEnabled || this.resourcesEnabled) {
      return this.getAvailableResourcesServices(values);
    }

    return of(false)
  }

  getAvailableResourcesServices(party): Observable<boolean> {
    const date = this.currentTime.format('x');
    const { services, resources, size, duration, type } = party;
    if (!date && !services && !resources) {
      return of(false);
    }

    const dateMilliseconds = this.dateTimeService.ngbDateToMoment(party.date, this.timezone).valueOf();
    const { hours, exceptions } = this.generalSchedule[0];
    const hoursWithDate = this.providerScheduleService.getWorkingHours(
      hours,
      exceptions,
      dateMilliseconds,
      this.timezone,
    );

    this.setEnabledState(false);

    return this.partiesService.findResourcesServicesForNewParty(
      date,
      type,
      services,
      resources,
      size,
      duration,
      party._id,
      this.overrideNoResources,
      hoursWithDate,
    )
      .pipe(
        take(1),
        switchMap((data) => {
          this.setEnabledState(true);
          this.showOverlappingAlert = false;
          this.overlappingParty = data?.overlappingParty || [];
          this.servicesInDropdown = data.availableServices || [];

          if (data?.availableResources) {
            this.resourcesInDropdown = data.availableResources.filter(resource => size >= resource.capacity.min);
            // If party.resource is already chosen and do not belong to resources list we need clear it
            if (this.resourcesInDropdown.length && party.resources.length) {
              this.getGroupField('resources').setValue(party.resources.filter(resourceId => this.resourcesInDropdown.some(resource => resource._id === resourceId)), {emitEvent: false});
            }

            if (this.resourcesEnabled) {
              this.showResourceAlert = true;
            }
          }

          if (this.servicesInDropdown?.length === 1 && !services.length && !this.removeServices) {
            const serviceIdToSelect = this.servicesInDropdown[0]._id;
            this.getGroupField('services').setValue([serviceIdToSelect], {emitEvent: false});
            this.resourcesInDropdown = this.resourcesInDropdown.filter(resource => resource.services.includes(serviceIdToSelect));

            if (this.user.resourceSettings?.enabled && this.resourcesInDropdown.length && !this.removeResources && !this.selectedResource) {
              let resourceIdToSelect: string;

              for (const availableResource of data?.availableResources) {
                if (
                  availableResource.services.includes(serviceIdToSelect) &&
                  size >= availableResource.capacity.min &&
                  size <= availableResource.capacity.max
                ) {
                  // We don't want to pre-select a resource that doesn't fit party size
                  // And we've already filtered resources by min capacity
                  resourceIdToSelect = availableResource._id;

                  break;
                }
              }

              if (resourceIdToSelect !== undefined) {
                this.getGroupField('resources').setValue([resourceIdToSelect], {emitEvent: false});
              }
            }
          }

          // this.resourceOverride.emit(party);
          return of(data);
        }),
        map(() => true),
        catchError((error) => {
          this.setEnabledState(true);
          if (error.status === 401) {
            this.cancel();
          } else {
            this.alertsService.add({
              time: Date.now(),
              type: IAlertTypes.danger,
              heading: 'Error:',
              body: `resources and services not found: ${error}`,
            });
          }

          return of(error);
        }),
      );
  }

  selectServices(services: string[] = undefined) {
    const {requestGroup: {services: prevServices}} = this.form.value;
    this.removeServices = prevServices.length && services?.length === 0;
    if (this.removeServices || prevServices.sort().toString() !== services.sort().toString()) {
      this.getGroupField('duration').setValue(0, {emitEvent: false});
    }
  }

  selectResources(resources?: string[]): void {
    const {requestGroup: {resources: prevResources}} = this.form.value;
    this.removeResources = prevResources.length > 0 && resources?.length === 0;
  }

  clearServicesResources() {
    this.form.patchValue({
      requestGroup: {
        resources: [],
        services: [],
        duration: 0,
      }
    }, {emitEvent: false});
  }

  setAvailableDuration() {
    this.getGroupField('duration').setValue(this.availablePartyDuration, {emitEvent: false});
    this.showOverlappingAlert = false;
    this.availablePartyDuration = 0;
    this.setFlags();
  }

  convertQuotedTime(party) {
    const resTime = moment.tz(this.timezone);

    delete party.date;
    delete party.time;
    delete party.quoted_time_minutes;
    delete party.className;
    delete party.assignedResources;

    if (party.type === PartyTypes.reservation && this.currentTime.format('x') < resTime.add(-this.reservationMinutesPast, 'minutes').format('x')) {
      party.quoted_time = Number(this.currentTime.format('x'));
      this.isWorkTime = false;

      return;
    }

    const updateWaitTime = !party._id && party.type === PartyTypes.walkIn; // Update wait time if new party
    party.quoted_time = party.quoted_time || 0;
    if (updateWaitTime && !(
      this.originalQuotedTime > 0 &&
      party.quoted_time > this.originalQuotedTime - MS_PER_MIN &&
      party.quoted_time < this.originalQuotedTime + MS_PER_MIN
    )) {
      this.waitTimeService.setTime(Math.round((party.quoted_time - Date.now()) / MS_PER_MIN));
    }
    party.quoted_time = Number(this.currentTime.format('x'));
  }

  showSizeAlert(resourcesCapacity: ICapacity): void {
    const size = this.getGroupField('size').value;
    const isMoreCapacity = (size > resourcesCapacity.max) || false;
    const isLessCapacity = (size < resourcesCapacity.min) || false;

    if (this.overlappingParty && this.allowMultipleParties) {
      const partiesInTimeSlot = this.overlappingParty;
      let count = partiesInTimeSlot.length;
      let newPartySize = 1;

      if (this.partySizeForTimeSlots) {
        count = partiesInTimeSlot.reduce((sum, party) => sum + (party.size || 0), 0);
        newPartySize = size;
      }

      if ((count + newPartySize) > resourcesCapacity.max) {
        this.setDeterminerValues(count + newPartySize - resourcesCapacity.max);
        this.setShowResourcesSizeAlert();

        return;
      }
    }

    if (isLessCapacity || isMoreCapacity) {
      if (isLessCapacity) {
        this.setDeterminerValues(resourcesCapacity.min - size);
      }
      if (isMoreCapacity) {
        this.setDeterminerValues(size - resourcesCapacity.max);
      }
      this.setShowResourcesSizeAlert();
    } else {
      this.showResourcesSizeAlert = false;
    }
  }

  getItemOptionName = (item: ResourceAssignedToFloorplan): string => {
    return this.getItemName(item, true);
  };

  getItemName(item: ResourceAssignedToFloorplan, addCapacity = false): string {
    let result = item?.name || '';

    if (addCapacity) {
      result += ` (${item.capacity.max}) `;
    }

    if (item?.onFloorplan) {
      result += ` [${item.onFloorplan}]`;
    }

    return result;
  }

  isConfirmPartyButtonDisabled() {
    return (this.showOverlappingAlert || this.showResourcesSizeAlert);
  }

  saveParty(): void {
    const {requestGroup, ...rest} = this.form.value;
    const party = this.partiesService.clean({
      ...rest,
      ...requestGroup
    });
    if (party.phone) {
      party.phone = this.telService.format(party.phone, this.user.country, 'intl');
    }

    this.convertQuotedTime(party);
    if (this.isOutOfBusinessHours() && !this.overrideIsWorkTime) {

      return;
    }

    party.overrideNoResources = this.overrideNoResources;

    if (!this.showOverlappingAlert && this.isEditMode) {
      party.overrideNoResources = true;
    }

    this.savePartyAction(party as IParty);
  }

  cancel(): void {
    void this.modalController.dismiss(null);
  }

  ok() {
    const { phone } = this.form.value;
    if (phone !== '' && this.parties.find(party => party.phone === phone)) {
      this.modalController.create<unknown, string>(this.confirmModalContentRef.nativeElement)
        .pipe(
          take(1),
          tap((res) => {
            if (res === 'Cancel click') {
              return;
            }

            this.actionAfterCloseModal();
          }),
          catchError((error) => {
            this.cancel();

            return of(error);
          }),
        )
        .subscribe();

      return;
    }
    this.actionAfterCloseModal();
  }

  actionAfterCloseModal(): void {
    const { size } = this.form.value;
    if (this.user.waitFields.size && size && typeof size !== 'number') {

      return;
    }

    this.saveParty();
  }

  getIsSubmitDisabled(): boolean {
    const isAvailableTimeAndNoOverride = this.isAvailableTime && !this.overrideNoResources;

    return this.form.invalid ||
      this.isOutOfBusinessHours() && !this.overrideIsWorkTime ||
      this.noAvailableServices ||
      this.isConfirmPartyButtonDisabled() ||
      this.form.pending ||
      this.isSubmitting ||
      isAvailableTimeAndNoOverride;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // this.resourceOverride.complete();
  }

  undoCheckIn(): void {
    const party = cloneDeep(this.partyToEdit);
    party.checkin_time = null;
    party.overrideNoResources = this.overrideNoResources;

    if (this.isEditMode) {
      party.overrideNoResources = true;
    }

    this.savePartyAction(party, 'check-in was canceled');
  }

  undoServed(): void {
    this.isSubmitting = true;
    const options = {
      party_id: this.partyToEdit._id,
      seated_time: null,
      canceled_time: null,
    };

    this.markPartyService.setParty(options)
      .pipe(
        tap(() => {
          this.alertsService.addSuccess(
            `moved to ${this.partyToEdit.type === PartyTypes.togo ? 'To Go' : 'waitlist'}`,
            'Success: ',
          );
          void this.modalController.dismiss();
          this.isSubmitting = false;
        }),
        catchError(err => {
          this.isSubmitting = false;
          this.alertsService.addError('an error occurred during moving party to Waitlist');

          return of(err);
        }),
      ).subscribe();
  }

  // TODO: move logic to from value changes
  checkIfOutOfBusinessHours(): boolean {
    return this.isOutOfBusinessHours() && !this.overrideIsWorkTime;
  }

  setShowResourcesSizeAlert() {
    if (!this.resourcesInDropdown?.length) {
      return;
    }

    this.showResourcesSizeAlert = true;
  }

  isWhatsAppEnabled() {
    return useWhatsApp(this.user);
  }

  getGroupField(name: string) {
    return this.form.get(`requestGroup.${name}`);
  }

  private savePartyAction(party: IParty, successMessage = 'updated successfully'): void {
    if (this.isSavingParty) {
      return;
    }

    this.isSubmitting = true;
    this.isSavingParty = true;
    this.partiesService.saveParty(party)
      .pipe(
        take(1),
        tap((data) => {
          void this.modalController.dismiss(data);
          this.alertsService.add({
            time: Date.now(),
            type: IAlertTypes.success,
            heading: 'Success:',
            body: successMessage,
          });
        }),
        catchError((err) => {
          if (err?.reloadData) {
            this.getGroupField('resources').setValue([], {onlySelf: true, emitEvent: false});
          }

          const customer = this.user.displayOptions?.labels?.customer || 'customer';
          this.alertsService.add({
            time: Date.now(),
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `new ${customer} not added successfully: ${err?.message}`,
          });

          return of(err);
        }),
        finalize(() => {
          this.isSavingParty = false;
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }

  private setDeterminerValues(delta: number): void {
    const { determiner, determinerChange, determinerFix } = this.partiesService.getDeterminerValues(delta);
    this.deltaResourcesSize = delta;
    this.determiner = determiner;
    this.determinerChange = determinerChange;
    this.determinerFix = determinerFix;
  }

  private initForm() {
    this.form = this.formBuilder.group({
      phone: ['', [this.validationService.phonePatternCountryValidator(this.user.country)]],
      name: ['', Validators.required],
      note: [''],
      messagingProvider: [null, []],
      customFields: [[]],
      // Changing each of these fields may trigger http request
      requestGroup: this.formBuilder.group({
        _id: [''],
        type: PartyTypes.reservation,
        size: [this.user.default_size, [Validators.required, Validators.min(1)]],
        date: ['', [Validators.required]],
        time: ['', [Validators.required]],
        services: [[]],
        resources: [[]],
        duration: [0, [Validators.required, Validators.min(0)]],
        overrideNoResources: [false, []],
        overrideIsWorkTime: [false, []],
      }),
    });

    this.form.get('messagingProvider').disable();

    this.subscriptions.add(this.listenToPhoneChanges().subscribe());

    const requestGroupSource = merge(
      this.listenToDateTimeChanges(),
      this.listenToServicesChanges(),
      this.listenToResourcesChanges(),
      this.getGroupField('size').valueChanges,
      this.getGroupField('duration').valueChanges,
      this.listenToOverrideNoResources(),
      this.listenToOverrideIsWorkTime(),
    ).pipe(
      switchMap(() => this.form.get('requestGroup').valueChanges),
      filter(() => !this.isSubmitting), // TODO: it's temporary solution, timepicker trigger changes twice, so it have to be fixed
      tap(() => this.isSubmitting = true),
      mergeMap(value => this.checkWorkTime(value)),
      tap(() => {
        this.handleAlerts();
        this.isSubmitting = false;
      }),
      catchError(error => {
        console.error(error);
        this.isSubmitting = false;

        return EMPTY;
      }),
    );

    const restColumnsSource = merge(
      this.form.get('phone').valueChanges,
      this.form.get('name').valueChanges,
      this.form.get('note').valueChanges,
      this.form.get('customFields').valueChanges,
    );

    const formSubscription = merge(
      requestGroupSource,
      restColumnsSource,
      this.form.statusChanges,
    )
      .pipe(tap(() => this.setFlags()))
      .subscribe();
    this.subscriptions.add(formSubscription);
  }

  private listenToPhoneChanges(): Observable<unknown> {
    return this.form.get('phone').valueChanges.pipe(
      switchMap(phone => forkJoin([
        of(phone),
        this.customerService.partyPhoneLookup(phone, this.user.country, this.user.partyFields, this.shortestPhoneDigits)
      ])),
      tap(([phone, data]) => {
        let dataToUpdate: Record<string, unknown> = {
          phone: this.telService.format(phone, this.user.country),
        };
        if (isUsOrCaPhoneNumber(phone)) {
          dataToUpdate.messagingProvider = null;
        }
        if (data) {
          dataToUpdate = {
            ...dataToUpdate,
            name: this.form.get('name').value || data.name,
            customFields: mergeCustomFields(this.form.value.customFields, data.customFields),
          };
        }
        this.form.patchValue(dataToUpdate, {emitEvent: false});

        if (!phone || isUsOrCaPhoneNumber(phone) || this.partyToEdit?.source === PartySource.api) {
          this.form.get('messagingProvider').disable({onlySelf: true, emitEvent: false});
        } else {
          this.form.get('messagingProvider').enable({onlySelf: true, emitEvent: false});
        }
      })
    )
  }

  private listenToDateTimeChanges() {
    return merge(
      this.getGroupField('date').valueChanges,
      this.getGroupField('time').valueChanges,
    )
      .pipe(
        tap(() => {
          const date = this.getGroupField('date').value;
          const time = this.getGroupField('time').value;
          if (this.selectedTimeMode || !time || !time.includes('M') || typeof date === 'string' || date === null) {
            return false;
          }

          this.currentTime = this.dateTimeService.setTimeToDate(
            this.dateTimeService.ngbDateToMoment(date, this.timezone),
            time,
            this.timezone,
          );

          if (this.servicesEnabled && this.onlineReservationEnabled && !this.resourcesEnabled) {
            this.clearServicesResources();
          }
          this.getGroupField('overrideIsWorkTime').setValue(false, {emitEvent: false});
          this.overrideIsWorkTime = false;
        }),
      );
  }

  private listenToServicesChanges() {
    return this.getGroupField('services').valueChanges
      .pipe(
        switchMap(services => {
          this.selectServices(services);

          return of(services);
        }),
      );
  }

  private listenToResourcesChanges() {
    return this.getGroupField('resources').valueChanges
      .pipe(switchMap(resources => {
        this.selectResources(resources);

        return of(resources);
      }))
  }

  private listenToOverrideNoResources() {
    return this.getGroupField('overrideNoResources').valueChanges.pipe(tap(value => {
      this.overrideNoResources = value;
    }));
  }

  private listenToOverrideIsWorkTime() {
    return this.getGroupField('overrideIsWorkTime').valueChanges.pipe(tap(value => {
      this.overrideIsWorkTime = value;
    }));
  }

  private setEnabledState(state: boolean) {
    // TODO: enable/disabled the whole group, now it causes error is size element
    // const control = this.form.get('requestGroup');
    // state ? control?.enable({emitEvent: false}) : control.disable({emitEvent: false});
    for (const controlName of this.lockedControlNames) {
      const control = this.form.get(`requestGroup.${controlName}`);
      state ? control?.enable({emitEvent: false}) : control.disable({emitEvent: false});
    }
  }

  private setFlags() {
    this.isAvailableTime = this.getIsAvailableTime();
    this.noAvailableServices = this.getNoAvailableServices();
    this.isSubmitDisabled = this.getIsSubmitDisabled();
  }

  private handleAlerts() {
    this.showResourcesSizeAlert = false;
    const resources = this.getGroupField('resources').value;
    if (this.resourcesEnabled && resources?.length) {
      const resourcesCapacity = this.partiesService.calculateCapacityInSelectedResources(this.resourcesInDropdown, resources);
      this.showSizeAlert(resourcesCapacity);
    }

    const date = this.currentTime.format('x');
    if (this.overlappingParty.length && this.onlineReservationEnabled && !(this.allowMultipleParties || this.overrideNoResources)) {
      this.showOverlappingAlert = true;
      const overlappingParty = this.overlappingParty[0];
      this.availablePartyDuration = overlappingParty.quoted_time - date;
      this.availablePartyDurationInMin = Math.round(this.availablePartyDuration / MS_PER_MIN);
    }

    let duration = this.getGroupField('duration').value;
    if (duration <= 0) {
      const services = this.getGroupField('services').value;
      duration = this.dateService.checkServicesDuration(this.servicesInDropdown, services);
      this.getGroupField('duration').setValue(duration, {emitEvent: false});
    }
  }
}
